import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Newspaper, Sparkles, Loader2, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

const IMPACT_ICON = {
  negative: { Icon: TrendingDown, color: "text-red-400" },
  positive: { Icon: TrendingUp, color: "text-emerald-400" },
  neutral: { Icon: Minus, color: "text-text-secondary" },
};

export default function MarketBriefingWidget() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/market-briefing");
      if (data?.headline) setBrief(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const regen = async () => {
    setGenerating(true);
    try {
      // GET endpoint auto-regenerates if cache expired; force by calling endpoint again
      const { data } = await api.get("/ai/market-briefing");
      setBrief(data);
      toast.success("Briefing refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="glass p-5" data-testid="market-briefing-widget">
      <div className="flex justify-between items-center mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Newspaper className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-text-secondary truncate">
            AI Market Briefing
          </span>
        </div>
        <button onClick={regen} disabled={generating || loading}
          data-testid="briefing-refresh-btn"
          className="text-text-secondary hover:text-amber-500 shrink-0" title="Refresh">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-data text-xs text-text-secondary animate-pulse">Analyzing market...</div>
      ) : !brief ? (
        <button onClick={regen} disabled={generating} data-testid="briefing-generate-btn"
          className="btn-tactile w-full inline-flex items-center justify-center gap-2 text-xs">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-amber-500" />}
          Generate Briefing
        </button>
      ) : (
        <div className="space-y-3">
          <div className="font-display text-base leading-snug">{brief.headline}</div>
          {brief.market_snapshot && (
            <div className="text-xs text-text-secondary leading-relaxed border-l-2 border-amber-500/30 ps-2">
              {brief.market_snapshot}
            </div>
          )}

          {brief.bid_impact?.length > 0 && (
            <div>
              <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-1.5">Bid Impact</div>
              <div className="space-y-1.5">
                {brief.bid_impact.slice(0, 3).map((b, i) => {
                  const cfg = IMPACT_ICON[b.impact] || IMPACT_ICON.neutral;
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <cfg.Icon className={`w-3 h-3 ${cfg.color} mt-0.5 shrink-0`} />
                      <div className="min-w-0">
                        <div className="font-mono-data text-[10px] truncate">{b.rfp_title}</div>
                        <div className="text-text-secondary text-[11px] leading-tight">{b.reasoning}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {brief.action_items?.length > 0 && (
            <div>
              <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-1.5">Actions</div>
              {brief.action_items.slice(0, 3).map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs leading-tight mb-1">
                  <ArrowRight className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          )}

          {brief.generated_at && (
            <div className="font-mono-data text-[9px] text-text-secondary/60 pt-1 border-t border-white/5">
              {new Date(brief.generated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
